/**
 * Achievement metadata for goal display and prerequisite management
 *
 * SINGLE SOURCE OF TRUTH FOR ACHIEVEMENT PREREQUISITES
 * ====================================================
 * This file defines the prerequisites for each achievement, including advanced mode.
 *
 * Advanced Mode Unlocking:
 * - Advanced mode is unlocked when all prerequisites for 'advanced-button' are met
 * - Use isAdvancedModeUnlocked() to check unlock status
 * - The problem tree structure in ProblemTreePanel.tsx should visually match these prerequisites
 *
 * Contextual Hints:
 * - Achievement-specific hints are defined in /components/hints/achievementHints.ts
 * - Hints analyze the current manipulative state to provide specific, encouraging guidance
 *
 * Consistency:
 * - validateProblemTreeConsistency() checks that the problem tree matches this metadata
 * - This validation runs in development mode to catch configuration errors
 */

export interface AchievementMetadata {
  id: string;
  icon: string;
  title: string;
  shortDescription: string;
  /** IDs of achievements that must be completed before this one */
  prerequisites: string[];
}

export const ACHIEVEMENT_METADATA: Record<string, AchievementMetadata> = {
  double: {
    id: 'double',
    icon: '✌️',
    title: 'Simple Ratios',
    shortDescription: 'Express a ratio of 1 to 2 using blocks',
    prerequisites: [],
  },
  '3-to-8': {
    id: '3-to-8',
    icon: '🎯',
    title: 'Non-Unit Ratios',
    shortDescription: 'Express a ratio of 3 to 8 using blocks',
    prerequisites: ['double'],
  },
  '8-to-3': {
    id: '8-to-3',
    icon: '🔄',
    title: 'Ratio Order',
    shortDescription: 'Express a ratio of 8 to 3 by reversing the order',
    prerequisites: ['3-to-8'],
  },
  bikes: {
    id: 'bikes',
    icon: '🚲',
    title: 'Contextual Ratios',
    shortDescription: 'Create a ratio between "bikes" and "tires" with a value of 1 to 2',
    prerequisites: ['double'],
  },
  'multi-bike': {
    id: 'multi-bike',
    icon: '🚴',
    title: 'Ratio Multiples',
    shortDescription:
      'Create multiples of the bikes to tires ratio (e.g., 2 bikes to 4 tires) using the →× multiples tool.',
    prerequisites: ['bikes'],
  },
  sequenced: {
    id: 'sequenced',
    icon: '🔗',
    title: 'Multi-Part Ratios',
    shortDescription: 'Use the sequenced arrangement to express a ratio of 1 to 2 to 3',
    prerequisites: ['double'],
  },
  'equivalent-ratios': {
    id: 'equivalent-ratios',
    icon: '✖️',
    title: 'Equivalent Ratios',
    shortDescription: 'Find 5 ratios that are equivalent to 5:9 (using the →× multiples tool)',
    prerequisites: ['multi-bike'],
  },
  spiders: {
    id: 'spiders',
    icon: '🕷️',
    title: 'Real-World Ratios',
    shortDescription: 'Express a ratio of "spiders" to "legs" (remember: spiders have 8 legs!)',
    prerequisites: ['bikes'],
  },
  'part-to-whole': {
    id: 'part-to-whole',
    icon: '🇺🇸',
    title: 'Part-to-Whole',
    shortDescription:
      'Express a ratio of red stripes to white stripes on the American flag. (So that 7 of the 13 total stripes are red.)',
    prerequisites: ['spiders', '8-to-3'],
  },
  'advanced-button': {
    id: 'advanced-button',
    icon: '✨',
    title: 'Advanced Mode Challenge',
    shortDescription: 'Complete the Equivalent Ratios and Part-to-Whole achievements to unlock',
    // ADVANCED MODE UNLOCK: Change these prerequisites to adjust what unlocks advanced mode
    // Current paths to unlock:
    //   - double → bikes → multi-bike → equivalent-ratios
    //   - double → 3-to-8 → 8-to-3 + (bikes → spiders) → part-to-whole
    // The problem tree structure auto-generates to reflect these prerequisites
    prerequisites: ['equivalent-ratios', 'part-to-whole'],
  },
};

/**
 * Get the achievement metadata by ID
 */
export function getAchievementMetadata(id: string): AchievementMetadata | undefined {
  return ACHIEVEMENT_METADATA[id];
}

/**
 * Check if all prerequisites for an achievement are met
 */
export function arePrerequisitesMet(achievementId: string, completedAchievements: Record<string, string>): boolean {
  const metadata = ACHIEVEMENT_METADATA[achievementId];
  if (!metadata) return false;

  return metadata.prerequisites.every(
    prereqId => completedAchievements[prereqId] && completedAchievements[prereqId] !== 'empty',
  );
}

/**
 * Find the first achievement that should be selected as the initial goal
 * This is the first achievement where all prerequisites are met
 */
export function getInitialSelectedGoal(completedAchievements: Record<string, string>): string {
  // Define the order to check achievements (roughly in problem tree order)
  const checkOrder = [
    'double',
    'bikes',
    'multi-bike',
    'equivalent-ratios',
    'spiders',
    'sequenced',
    '3-to-8',
    '8-to-3',
    'boss-button',
  ];

  for (const achievementId of checkOrder) {
    if (arePrerequisitesMet(achievementId, completedAchievements)) {
      // If this achievement itself is not yet complete, it's the goal
      if (!completedAchievements[achievementId] || completedAchievements[achievementId] === 'empty') {
        return achievementId;
      }
    }
  }

  // Default to the root achievement
  return 'double';
}

/**
 * Check if Advanced Mode should be unlocked
 * Advanced Mode is unlocked when all prerequisites for the 'advanced-button' achievement are met
 */
export function isAdvancedModeUnlocked(completedAchievements: Record<string, string>): boolean {
  return arePrerequisitesMet('advanced-button', completedAchievements);
}

/**
 * Generate problem tree structure from achievement prerequisites
 * This inverts the prerequisite relationships (bottom-up) into parent-child relationships (top-down)
 *
 * Uses topological sorting to ensure nodes appear in dependency order
 *
 * SINGLE SOURCE OF TRUTH: This ensures the tree structure always matches the prerequisites
 */
export function generateProblemTreeStructure(): { id: string; children: string[] }[] {
  const allAchievementIds = Object.keys(ACHIEVEMENT_METADATA);

  // Build parent → children mapping (inverse of prerequisites)
  const parentToChildrenMap = new Map<string, Set<string>>();

  // Initialize all achievements in the map
  for (const achievementId of allAchievementIds) {
    parentToChildrenMap.set(achievementId, new Set());
  }

  // Build the parent-child relationships by inverting prerequisites
  for (const [achievementId, metadata] of Object.entries(ACHIEVEMENT_METADATA)) {
    for (const prereqId of metadata.prerequisites) {
      if (!parentToChildrenMap.has(prereqId)) {
        parentToChildrenMap.set(prereqId, new Set());
      }
      parentToChildrenMap.get(prereqId)!.add(achievementId);
    }
  }

  // Topological sort using Kahn's algorithm to get proper ordering
  // This ensures nodes with multiple parents appear after ALL their parents
  const inDegree = new Map<string, number>();

  // Calculate in-degree (number of prerequisites) for each node
  for (const achievementId of allAchievementIds) {
    const metadata = ACHIEVEMENT_METADATA[achievementId];
    inDegree.set(achievementId, metadata.prerequisites.length);
  }

  // Start with nodes that have no prerequisites (in-degree = 0)
  const queue: string[] = [];
  for (const achievementId of allAchievementIds) {
    if (inDegree.get(achievementId) === 0) {
      queue.push(achievementId);
    }
  }

  if (queue.length === 0) {
    console.error('[generateProblemTreeStructure] No root achievement found (all have prerequisites)!');
    return [];
  }

  // Process nodes in topological order
  const orderedNodes: { id: string; children: string[] }[] = [];
  const processed = new Set<string>();

  while (queue.length > 0) {
    const nodeId = queue.shift()!;

    if (processed.has(nodeId)) continue;
    processed.add(nodeId);

    // Get children and sort them alphabetically for consistent ordering
    const children = Array.from(parentToChildrenMap.get(nodeId) || []).sort();

    orderedNodes.push({
      id: nodeId,
      children,
    });

    // Decrease in-degree for all children
    for (const childId of children) {
      const currentInDegree = inDegree.get(childId)! - 1;
      inDegree.set(childId, currentInDegree);

      // If all prerequisites are satisfied (in-degree reaches 0), add to queue
      if (currentInDegree === 0 && !processed.has(childId)) {
        queue.push(childId);
      }
    }
  }

  // Check if we processed all nodes
  if (processed.size !== allAchievementIds.length) {
    const missedNodes = allAchievementIds.filter(id => !processed.has(id));
    console.error('[generateProblemTreeStructure] Cycle detected or disconnected nodes:', missedNodes);

    // Add disconnected nodes at the end as a fallback
    for (const nodeId of missedNodes) {
      const children = Array.from(parentToChildrenMap.get(nodeId) || []).sort();
      orderedNodes.push({
        id: nodeId,
        children,
      });
    }
  }

  return orderedNodes;
}

/**
 * Validate that the problem tree structure is consistent with achievement prerequisites
 * This helps catch configuration errors early
 *
 * @deprecated This validation is no longer needed since the tree is auto-generated from prerequisites
 */
export function validateProblemTreeConsistency(problemTreeStructure: { id: string; children?: string[] }[]): {
  isValid: boolean;
  errors: string[];
} {
  const errors: string[] = [];
  const nodeIds = new Set(problemTreeStructure.map(node => node.id));

  // Check that all achievements referenced in the tree have metadata
  for (const node of problemTreeStructure) {
    if (node.id !== 'advanced-button' && !ACHIEVEMENT_METADATA[node.id]) {
      errors.push(`Node '${node.id}' in problem tree has no achievement metadata`);
    }
  }

  // Check that all achievements in metadata are in the tree
  for (const achievementId of Object.keys(ACHIEVEMENT_METADATA)) {
    if (!nodeIds.has(achievementId)) {
      errors.push(`Achievement '${achievementId}' has metadata but is not in problem tree`);
    }
  }

  // Check that advanced-button prerequisites match what's expected
  const advancedMetadata = ACHIEVEMENT_METADATA['advanced-button'];
  if (advancedMetadata) {
    const advancedNode = problemTreeStructure.find(n => n.id === 'advanced-button');
    if (!advancedNode) {
      errors.push('advanced-button node not found in problem tree');
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

/**
 * ORDERED LIST OF ACHIEVEMENTS FOR BADGE DISPLAY
 * This defines the order badges appear in the badge array passed to ProblemTreePanel
 *
 * IMPORTANT: This order must match the order badges are defined in App.tsx badgeSections
 * When adding a new achievement:
 * 1. Add it to ACHIEVEMENT_METADATA above
 * 2. Add it to this array in the correct position
 * 3. Add the badge configuration to App.tsx badgeSections in the same position
 */
export const ACHIEVEMENT_DISPLAY_ORDER: string[] = [
  'double',
  '3-to-8',
  '8-to-3',
  'bikes',
  'multi-bike',
  'sequenced',
  'equivalent-ratios',
  'spiders',
  'part-to-whole',
];

/**
 * Create a mapping of achievement IDs to their display index
 * This is used by ProblemTreePanel to map tree nodes to badge array indices
 */
export function createAchievementIndexMapping(): Record<string, number> {
  const mapping: Record<string, number> = {};
  ACHIEVEMENT_DISPLAY_ORDER.forEach((id, index) => {
    mapping[id] = index;
  });
  return mapping;
}

/**
 * Validate that all achievements in ACHIEVEMENT_DISPLAY_ORDER exist in metadata
 * and that no achievements are missing from the display order
 */
export function validateAchievementDisplayOrder(): { isValid: boolean; errors: string[] } {
  const errors: string[] = [];

  // Check that all IDs in display order have metadata (excluding advanced-button which isn't displayed)
  for (const id of ACHIEVEMENT_DISPLAY_ORDER) {
    if (!ACHIEVEMENT_METADATA[id]) {
      errors.push(`Achievement '${id}' in ACHIEVEMENT_DISPLAY_ORDER has no metadata`);
    }
  }

  // Check that all achievements (except advanced-button) are in the display order
  const displayOrderSet = new Set(ACHIEVEMENT_DISPLAY_ORDER);
  for (const id of Object.keys(ACHIEVEMENT_METADATA)) {
    if (id !== 'advanced-button' && !displayOrderSet.has(id)) {
      errors.push(`Achievement '${id}' has metadata but is missing from ACHIEVEMENT_DISPLAY_ORDER`);
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}
